import { Modal } from '../Modal';
import { AuditTrailList } from './AuditTrailList';

/**
 * Per-module audit history — frontend reference §13.
 *
 * The same detail depth as the global page: every row opens the shared
 * `<AuditLogDetailModal>`. Scoped by `entityType` (CSV) rather than a per-module endpoint,
 * and paginated — never a fixed "Latest N" window, which silently hides history the moment
 * a module gets busy.
 *
 * **The list is `<AuditTrailList>`**, shared with a profile's in-place trail. This modal is its
 * host: it MOUNTS the list while open, which is what fetches it and what resets its search and
 * page on close.
 */
export function HistoryLogsModal({
  open,
  onClose,
  module,
  entityTypes,
  entityId,
}: {
  open: boolean;
  onClose: () => void;
  module: string;
  /**
   * MODULE history: every type the module's workflow touches — parent AND children, or the
   * sections of a record are missing from it. Pass the module's one shared list, never a
   * literal, so two surfaces cannot disagree about what the module writes.
   *
   * Leave it off for a RECORD's history — see `entityId`.
   */
  entityTypes?: string[];
  /**
   * RECORD history: every row about this record **or about one of its sections**, which the
   * server resolves through the audit row's parent.
   *
   * **Scope by id OR by type, not both.** A record is identified by its id, and adding the
   * type bound on top of it only subtracts: a write the server refused never reached an
   * entity, so it carries no `entityType` and a type filter drops it — and somebody trying to
   * change this record and being refused is exactly the event its history should show.
   */
  entityId?: string;
}) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="History Logs"
      size="2xl"
      id={`${module}-history-modal`}
      // The list owns the scroll so the header and footer stay put.
      bodyScroll={false}
    >
      {open ? (
        <AuditTrailList
          module={module}
          entityTypes={entityTypes}
          entityId={entityId}
          emptyMessage={entityId ? 'No recorded activity for this record yet.' : 'No recorded activity for this module yet.'}
        />
      ) : null}
    </Modal>
  );
}
